<script lang="ts">
    import { Modal } from "@/components";
    import { translate } from "@/utils/format";
    import type {
        SponsoredFailReason,
        SponsoredLookupResult,
        SponsoredTarget,
    } from "@/services/sponsored";
    import { getDashboardCtx } from "../../state/dashboard.svelte";
    import { getSponsoredCtx } from "../../state/sponsored.svelte";
    import { formatMoney } from "../../lib/format";
    import { reasonMessage } from "../../lib/sponsoredCopy";

    let { open, onClose }: { open: boolean; onClose: () => void } = $props();

    const store = getDashboardCtx();
    const sponsored = getSponsoredCtx();

    type Step = "pick" | "confirm" | "done";
    type Mode = "clients" | "email";

    let step = $state<Step>("pick");
    let mode = $state<Mode>("clients");
    let selectedId = $state<string | null>(null);
    let email = $state("");
    let found = $state<Extract<SponsoredLookupResult, { ok: true }> | null>(null);
    let failure = $state<SponsoredFailReason | null>(null);
    let busy = $state(false);
    let idemKey = "";

    /** Registered users with a real id that we aren't already paying for. */
    const eligible = $derived(
        store.usersList.filter(
            (u) =>
                u.status === "registered" &&
                !u.id.startsWith("usr-") &&
                !sponsored.sponsoredUserIds.has(u.id),
        ),
    );

    const canContinue = $derived(
        mode === "clients"
            ? !!selectedId
            : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    );

    // Fresh state every time the modal opens.
    $effect(() => {
        if (!open) return;
        step = "pick";
        mode = eligible.length ? "clients" : "email";
        selectedId = null;
        email = "";
        found = null;
        failure = null;
        busy = false;
    });

    function target(): SponsoredTarget {
        if (found) return { userId: found.userId };
        return mode === "clients"
            ? { userId: selectedId! }
            : { email: email.trim() };
    }

    async function next() {
        if (!canContinue || busy) return;
        busy = true;
        failure = null;
        try {
            const res = await sponsored.lookup(
                mode === "clients"
                    ? { userId: selectedId! }
                    : { email: email.trim() },
            );
            if (!res.ok) {
                failure = res.reason;
                return;
            }
            found = res;
            idemKey = crypto.randomUUID();
            step = "confirm";
        } catch (err) {
            console.error("❌ sponsored lookup error:", err);
            failure = "unknown";
        } finally {
            busy = false;
        }
    }

    async function confirm() {
        if (busy) return;
        busy = true;
        failure = null;
        try {
            const res = await sponsored.create(target(), idemKey);
            if (!res.ok) {
                failure = res.reason;
                if (res.reason === "requires_action" && res.hostedInvoiceUrl) {
                    window.open(res.hostedInvoiceUrl, "_blank");
                }
                return;
            }
            step = "done";
        } catch (err) {
            console.error("❌ sponsored create error:", err);
            failure = "unknown";
        } finally {
            busy = false;
        }
    }

    function back() {
        step = "pick";
        failure = null;
        found = null;
    }

    const pm = $derived(sponsored.billing.paymentMethod);
</script>

{#snippet content()}
    <div class="sponsorModal" aria-busy={busy}>
        {#if !sponsored.billing.hasDefaultPaymentMethod}
            <div class="sponsorModal__block">
                <p>
                    {translate(
                        "PARTNER_SPONSORED_NEED_PM",
                        "Para pagar la suscripción de un usuario primero tienes que guardar un método de pago.",
                    )}
                </p>
                <button
                    type="button"
                    class="button --primary"
                    disabled={sponsored.portalBusy}
                    onclick={sponsored.startSetup}
                >
                    {translate("PARTNER_BILLING_ADD", "Añadir método de pago")}
                </button>
            </div>
        {:else if step === "pick"}
            <div class="segmented" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "clients"}
                    class:isActive={mode === "clients"}
                    onclick={() => {
                        mode = "clients";
                        failure = null;
                    }}
                >
                    {translate("PARTNER_SPONSORED_MODE_CLIENTS", "Mis clientes")}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "email"}
                    class:isActive={mode === "email"}
                    onclick={() => {
                        mode = "email";
                        failure = null;
                    }}
                >
                    {translate("PARTNER_SPONSORED_MODE_EMAIL", "Por email")}
                </button>
            </div>

            {#if mode === "clients"}
                {#if eligible.length}
                    <ul class="sponsorModal__list" role="radiogroup">
                        {#each eligible as u (u.id)}
                            <li>
                                <label class:isSelected={selectedId === u.id}>
                                    <input
                                        type="radio"
                                        name="sponsorUser"
                                        value={u.id}
                                        bind:group={selectedId}
                                    />
                                    <span class="mono">{u.emailMasked}</span>
                                    <small>
                                        {translate(
                                            "PARTNER_SPONSORED_SINCE",
                                            "Registro",
                                        )}
                                        {store.formatShortDay(u.firstSeen)}
                                    </small>
                                </label>
                            </li>
                        {/each}
                    </ul>
                {:else}
                    <p class="sponsorModal__empty">
                        {translate(
                            "PARTNER_SPONSORED_NO_CLIENTS",
                            "No tienes clientes registrados disponibles. Prueba a buscar a alguien por su email.",
                        )}
                    </p>
                {/if}
            {:else}
                <label class="sponsorModal__field">
                    <span>
                        {translate("PARTNER_SPONSORED_EMAIL", "Email del usuario")}
                    </span>
                    <input
                        type="email"
                        inputmode="email"
                        aria-invalid={failure ? "true" : undefined}
                        aria-describedby={failure ? "sponsorError" : undefined}
                        autocomplete="off"
                        placeholder="nombre@correo.com"
                        bind:value={email}
                        onkeydown={(e) => e.key === "Enter" && next()}
                    />
                </label>
                <p class="sponsorModal__note">
                    {translate(
                        "PARTNER_SPONSORED_EMAIL_NOTE",
                        "La persona ya debe estar registrada en Caloo.",
                    )}
                </p>
            {/if}

            {#if failure}
                <p class="sponsorModal__error" id="sponsorError" role="alert">
                    {reasonMessage(failure)}
                </p>
            {/if}

            <div class="sponsorModal__actions">
                <button type="button" class="button --cancel" onclick={onClose}>
                    {translate("LANG_CANCEL_BTN", "Cancelar")}
                </button>
                <button
                    type="button"
                    class="button --primary"
                    disabled={!canContinue || busy}
                    onclick={next}
                >
                    {busy
                        ? translate("PARTNER_SPONSORED_CHECKING", "Comprobando…")
                        : translate("PARTNER_SPONSORED_CONTINUE", "Continuar")}
                </button>
            </div>
        {:else if step === "confirm" && found}
            <dl class="sponsorModal__summary">
                <div>
                    <dt>{translate("PARTNER_SPONSORED_USER", "Usuario")}</dt>
                    <dd class="mono">{found.emailMasked}</dd>
                </div>
                {#if found.price}
                    <div>
                        <dt>{translate("PARTNER_SPONSORED_PRICE", "Precio")}</dt>
                        <dd>
                            {formatMoney(
                                found.price.amount,
                                found.price.currency,
                                store.uiLocale,
                            )}
                            /
                            {found.price.interval === "year"
                                ? translate("PARTNER_SPONSORED_YEAR", "año")
                                : translate("PARTNER_SPONSORED_MONTH", "mes")}
                        </dd>
                    </div>
                {/if}
                {#if pm}
                    <div>
                        <dt>{translate("PARTNER_SPONSORED_CHARGED_TO", "Se cobrará a")}</dt>
                        <dd class="mono">
                            {pm.type === "sepa_debit" ? "SEPA" : (pm.brand || "").toUpperCase()}
                            ···· {pm.last4}
                        </dd>
                    </div>
                {/if}
            </dl>
            {#if found.linkState === "unlinked"}
                <p class="sponsorModal__note">
                    {translate(
                        "PARTNER_SPONSORED_WILL_LINK",
                        "Esta persona se vinculará a tu centro al pagar.",
                    )}
                </p>
            {/if}
            <p class="sponsorModal__note">
                {translate(
                    "PARTNER_SPONSORED_RENEWS_NOTE",
                    "Se renueva cada periodo hasta que la canceles. Si la cancelas, la persona mantiene el acceso hasta el final del periodo ya pagado.",
                )}
            </p>

            {#if failure}
                <p class="sponsorModal__error" id="sponsorError" role="alert">
                    {reasonMessage(failure)}
                </p>
            {/if}

            <div class="sponsorModal__actions">
                <button type="button" class="button --cancel" disabled={busy} onclick={back}>
                    {translate("PARTNER_SPONSORED_BACK", "Atrás")}
                </button>
                <button
                    type="button"
                    class="button --primary"
                    disabled={busy}
                    onclick={confirm}
                >
                    {busy
                        ? translate("PARTNER_SPONSORED_PAYING", "Procesando…")
                        : translate("PARTNER_SPONSORED_CONFIRM", "Confirmar y pagar")}
                </button>
            </div>
        {:else if step === "done"}
            <div class="sponsorModal__block">
                <p class="success">
                    {translate(
                        "PARTNER_SPONSORED_DONE",
                        "Listo. La suscripción ya está activa y la pagas tú.",
                    )}
                </p>
                <button type="button" class="button --primary" onclick={onClose}>
                    {translate("PARTNER_SPONSORED_CLOSE", "Cerrar")}
                </button>
            </div>
        {/if}
    </div>
{/snippet}

<Modal
    isModalOpen={open}
    centered={true}
    className="sponsorModalWrap"
    title={translate("PARTNER_SPONSORED_ADD_TITLE", "Pagar suscripción de un usuario")}
    {onClose}
    onAccept={() => {}}
    showButtons={false}
    cancelable={true}
    {content}
    infoButton={() => {}}
/>
